"use client";

import { useCallback, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Briefcase,
  Check,
  Copy,
  Eye,
  KeyRound,
  ListChecks,
  Radar,
  Route,
  TerminalSquare,
  Timer,
  Wallet,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import type { DealworkRadar, MissionData, MissionState, Monitoring, SteRadar } from "@/types/mission";

/* ---------------------------------- utils ---------------------------------- */

export function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(seconds)) return "—";
  if (seconds < 60) return `${Math.max(seconds, 0)}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function statusStyles(status: string): { label: string; className: string } {
  switch (status) {
    case "active":
      return {
        label: "ACTIVE",
        className: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
      };
    case "validated":
      return {
        label: "VIABLE",
        className: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
      };
    case "validated-with-blockers":
      return {
        label: "VIABLE*",
        className: "border-amber-500/40 bg-amber-500/10 text-amber-400",
      };
    case "blocked":
      return {
        label: "BLOCKED",
        className: "border-orange-500/40 bg-orange-500/10 text-orange-400",
      };
    case "researching":
      return {
        label: "RESEARCHING",
        className: "border-zinc-500/40 bg-zinc-500/10 text-zinc-400",
      };
    case "rejected":
      return {
        label: "REJECTED",
        className: "border-rose-500/40 bg-rose-500/10 text-rose-400",
      };
    case "dead":
      return {
        label: "DEAD",
        className: "border-zinc-600/40 bg-zinc-700/20 text-zinc-500",
      };
    default:
      return {
        label: status.toUpperCase(),
        className: "border-zinc-600/40 bg-zinc-700/20 text-zinc-400",
      };
  }
}

export function StatusBadge({ status }: { status: string }) {
  const s = statusStyles(status);
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider ${s.className}`}
    >
      {s.label}
    </span>
  );
}

function useCopy() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const { toast } = useToast();
  const copy = useCallback(
    async (key: string, text: string, label?: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedKey(key);
        toast({
          title: `${label ?? "Value"} copied`,
          description: `${text.slice(0, 18)}…${text.slice(-6)}`,
        });
        window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1600);
      } catch {
        toast({ title: "Copy failed", description: "Clipboard unavailable in this context" });
      }
    },
    [toast],
  );
  return { copiedKey, copy };
}

/* ------------------------------ reality banner ----------------------------- */

export function RealityBanner({ data }: { data: MissionData }) {
  const verified = data.earnings.filter((e) => e.status === "verified").length;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-amber-200/90 sm:flex-row sm:items-center sm:gap-3"
      role="note"
    >
      <AlertTriangle className="size-5 shrink-0 text-amber-400" aria-hidden="true" />
      <p className="text-xs leading-relaxed sm:text-sm">
        <span className="font-semibold tracking-wide text-amber-300">REALITY CHECK — </span>
        This dashboard renders only verified mission state. An earning counts as{" "}
        <span className="font-semibold">verified</span> only with on-chain or platform evidence
        (txid / link). Verified earnings so far:{" "}
        <span className="font-mono font-semibold text-amber-300">{verified}</span>. Everything in
        the pipeline below is <span className="font-semibold">pursued, not yet earned</span>. Nothing
        on this page is simulated.
      </p>
    </motion.div>
  );
}

/* ---------------------------------- KPIs ----------------------------------- */

function KpiCard({
  label,
  value,
  sub,
  icon,
  accent,
  delay,
}: {
  label: string;
  value: string;
  sub: string;
  icon: React.ReactNode;
  accent: string;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35, ease: "easeOut" }}
    >
      <Card className="border-zinc-800/70 bg-zinc-900/40 shadow-none transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-500/25 hover:shadow-[0_8px_24px_-12px_rgba(16,185,129,0.25)] motion-reduce:transition-none">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
              {label}
            </p>
            <span className={`rounded-md border p-1.5 ${accent}`}>{icon}</span>
          </div>
          <p className="mt-2 font-mono text-2xl font-bold tracking-tight text-zinc-50">{value}</p>
          <p className="mt-1 text-[11px] leading-snug text-zinc-500">{sub}</p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function KpiGrid({ data }: { data: MissionData }) {
  const viable = data.pipeline.filter(
    (o) => o.status === "validated" || o.status === "validated-with-blockers" || o.status === "active",
  ).length;
  const pending = data.earnings.filter((e) => e.status === "pending" || e.status === "unverified").length;
  const usd = data.state.verified_earnings_total.usd_estimate;
  const monitored = data.monitoring?.results.filter((r) => r.ok).length ?? 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 lg:gap-4">
      <KpiCard
        label="Verified Earnings"
        value={usd > 0 ? `$${usd.toFixed(2)}` : "$0.00"}
        sub="on-chain evidence only"
        icon={<Wallet className="size-3.5" />}
        accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        delay={0}
      />
      <KpiCard
        label="Pending / Unverified"
        value={String(pending)}
        sub="awaiting evidence"
        icon={<Timer className="size-3.5" />}
        accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        delay={0.05}
      />
      <KpiCard
        label="Viable Paths"
        value={`${viable}`}
        sub={`of ${data.pipeline.length} tracked`}
        icon={<Route className="size-3.5" />}
        accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        delay={0.1}
      />
      <KpiCard
        label="Actions Logged"
        value={String(data.actions.length)}
        sub="verified executions"
        icon={<ListChecks className="size-3.5" />}
        accent="border-zinc-600/40 bg-zinc-700/20 text-zinc-300"
        delay={0.15}
      />
      <KpiCard
        label="Autonomous Sessions"
        value={String(data.state.session_count)}
        sub="continues every 6h"
        icon={<TerminalSquare className="size-3.5" />}
        accent="border-zinc-600/40 bg-zinc-700/20 text-zinc-300"
        delay={0.2}
      />
      <KpiCard
        label="Wallets Watched"
        value={`${monitored}/5`}
        sub={data.monitoring ? `checked ${timeAgo(data.monitoring.checked_at)}` : "baseline 0"}
        icon={<Eye className="size-3.5" />}
        accent="border-zinc-600/40 bg-zinc-700/20 text-zinc-300"
        delay={0.25}
      />
    </div>
  );
}

/* ------------------------------- loop stepper ------------------------------- */

const PHASE_TO_STEP: Record<string, number> = {
  RESEARCH: 0,
  "EXECUTION-PREP": 2,
  EXECUTION: 3,
  VERIFYING: 4,
  LEARNING: 5,
  ADAPTATION: 6,
};

export function LoopStepper({ phase }: { phase: string }) {
  const steps = ["OBSERVE", "REASON", "PRIORITIZE", "ACT", "VERIFY", "LEARN", "ADAPT", "REPEAT"];
  const current = PHASE_TO_STEP[phase] ?? 0;
  return (
    <Card className="border-zinc-800/70 bg-zinc-900/40 shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="flex min-w-0 items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          Operating Loop
          <span className="min-w-0 truncate font-normal normal-case tracking-normal text-zinc-600">
            every session runs this cycle
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0">
        <ol
          className="flex flex-wrap items-center gap-x-1 gap-y-2"
          aria-label="Mission operating loop"
        >
          {steps.map((step, i) => {
            const state = i < current ? "done" : i === current ? "current" : "future";
            return (
              <li key={step} className="flex items-center gap-1">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] font-semibold tracking-wider ${
                    state === "current"
                      ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                      : state === "done"
                        ? "border-emerald-500/25 bg-emerald-500/5 text-emerald-500/80"
                        : "border-zinc-700/60 bg-zinc-800/30 text-zinc-600"
                  }`}
                >
                  {state === "done" && <Check className="size-3" aria-hidden="true" />}
                  {step}
                </span>
                {i < steps.length - 1 && (
                  <span className="text-zinc-700" aria-hidden="true">
                    →
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

/* ------------------------------- strategy card ------------------------------ */

export function StrategyCard({ state }: { state: MissionState }) {
  return (
    <Card className="h-full border-zinc-800/70 bg-zinc-900/40 shadow-none transition-colors duration-200 hover:border-zinc-700/80">
      <CardHeader className="pb-3">
        <CardTitle className="flex min-w-0 items-center justify-between gap-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          Current Strategy
        </CardTitle>
      </CardHeader>
      <CardContent className="flex h-[calc(100%-3.4rem)] flex-col gap-4">
        <p className="text-sm leading-relaxed text-zinc-300">{state.strategy_summary}</p>
        <Separator className="bg-zinc-800/70" />
        <div>
          <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
            Hard Constraints
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {state.constraints.map((c) => (
              <li
                key={c}
                className="rounded-md border border-zinc-700/60 bg-zinc-800/30 px-2 py-1 text-[10px] leading-tight text-zinc-400"
                title={c}
              >
                {c.length > 42 ? `${c.slice(0, 42)}…` : c}
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------- wallets card ------------------------------ */

const CHAIN_META: Array<{ key: string; label: string; dot: string; monitorKey: string }> = [
  { key: "bitcoin", label: "Bitcoin", dot: "bg-amber-400", monitorKey: "bitcoin" },
  { key: "evm", label: "EVM / MetaMask", dot: "bg-emerald-400", monitorKey: "evm/ethereum" },
  { key: "solana", label: "Solana", dot: "bg-fuchsia-400", monitorKey: "solana" },
  { key: "tron", label: "TRON", dot: "bg-rose-400", monitorKey: "tron" },
  { key: "ronin", label: "Ronin", dot: "bg-lime-400", monitorKey: "ronin" },
];

export function WalletsCard({ state, monitoring }: { state: MissionState; monitoring: Monitoring | null }) {
  const { copiedKey, copy } = useCopy();
  const baselineDays = state.wallet_baseline?.checked_at
    ? Math.max(
        0,
        Math.floor((Date.now() - new Date(state.wallet_baseline.checked_at).getTime()) / 86400000),
      )
    : null;
  return (
    <Card className="h-full border-zinc-800/70 bg-zinc-900/40 shadow-none transition-colors duration-200 hover:border-zinc-700/80">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between font-mono text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          Receiving Wallets
          <Badge variant="outline" className="border-zinc-700 bg-zinc-800/40 font-mono text-[10px] text-zinc-500">
            receive-only
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0 space-y-2.5">
        {CHAIN_META.map(({ key, label, dot, monitorKey }) => {
          const address = state.wallets[key];
          if (!address) return null;
          const result = monitoring?.results.find((r) => r.chain === monitorKey);
          const short = `${address.slice(0, 8)}…${address.slice(-6)}`;
          return (
            <div
              key={key}
              className="group flex items-center justify-between gap-2 rounded-md border border-zinc-800/60 bg-zinc-950/40 px-3 py-2 transition-colors hover:border-zinc-700"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span className={`size-2 shrink-0 rounded-full ${dot}`} aria-hidden="true" />
                <div className="min-w-0">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    {label}
                  </p>
                  <p className="truncate font-mono text-xs text-zinc-300" title={address}>
                    {short}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`font-mono text-[11px] ${
                    result?.ok ? "text-emerald-400" : "text-zinc-600"
                  }`}
                  title={result?.ok ? `balance: ${result.balance}` : (result?.detail ?? "no check available")}
                >
                  {result?.ok ? (result.balance ?? "0") : "—"}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-zinc-500 hover:text-zinc-200"
                  onClick={() => copy(key, address, `${label} address`)}
                  aria-label={`Copy ${label} address`}
                >
                  {copiedKey === key ? (
                    <Check className="size-3.5 text-emerald-400" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </Button>
              </div>
            </div>
          );
        })}
        <div className="pt-1 text-[11px] leading-snug text-zinc-600">
          Balances from public block APIs{monitoring ? ` · last check ${timeAgo(monitoring.checked_at)}` : ""}.
          {baselineDays !== null && (
            <>
              {" "}
              <span className="text-zinc-500">
                {baselineDays} day{baselineDays === 1 ? "" : "s"} at zero since baseline{" "}
                {(state.wallet_baseline?.checked_at ?? "").slice(0, 10)} — any non-zero balance is a
                real earnings event.
              </span>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------ ops / autonomy ------------------------------ */

function scheduleLabel(s: { name: string; kind: string; every_seconds?: number; expr?: string; tz?: string }): string {
  if (s.kind === "fixed_rate") {
    const h = (s.every_seconds ?? 0) / 3600;
    return h >= 1 ? `every ${h % 1 === 0 ? h : h.toFixed(1)}h` : `every ${Math.round(s.every_seconds ?? 0)}s`;
  }
  if (s.kind === "cron" && s.expr) {
    const m = s.expr.match(/^\d+ \d+ (\d+) \* \* \?$/);
    if (m) return `daily ${String(m[1]).padStart(2, "0")}:00 ${s.tz === "Asia/Kolkata" ? "IST" : (s.tz ?? "")}`;
    return s.expr;
  }
  return s.kind;
}

/**
 * Honest countdown to the next fire of a cron job, computed from the schedule
 * metadata alone (no last-run state is recorded, so recurring cadence + IST
 * clock math is all we can truthfully show). Returns null when not computable.
 */
function nextFireCountdown(expr: string | undefined, tz: string | undefined): string | null {
  if (!expr) return null;
  const zone = tz ?? "Asia/Kolkata";
  const daily = expr.match(/^\d+ \d+ (\d+) \* \* \?$/);
  if (daily) {
    const targetHour = parseInt(daily[1], 10);
    try {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: zone,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      })
        .format(new Date())
        .split(":");
      const nowMin = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
      let diff = targetHour * 60 - nowMin;
      if (diff <= 0) diff += 24 * 60;
      const h = Math.floor(diff / 60);
      const m = diff % 60;
      return `next in ${h}h ${String(m).padStart(2, "0")}m`;
    } catch {
      return null;
    }
  }
  const every15 = expr.match(/^0 \*\/15 \* \* \* \?$/);
  if (every15) return "every 15 min";
  return null;
}

export function OpsCard({
  state,
  radar,
  dealwork,
}: {
  state: MissionState;
  radar: SteRadar | null;
  dealwork: DealworkRadar | null;
}) {
  const openCount = radar?.open_listings?.length ?? 0;
  const newCount = radar?.new_since_last?.length ?? 0;
  // `!= null` covers both null and undefined (defensive against a missing prop
  // mid-deploy) — `!== null` alone would treat undefined as "data available".
  const radarOk = radar != null;
  const dwOk = dealwork != null;
  const dwReal = dealwork?.real_tasks?.length ?? 0;
  const dwAds = dealwork?.ad_posts ?? 0;
  const dwPool = dealwork?.bidding_pool_total ?? 0;
  const dwNew = dealwork?.new_since_last?.length ?? 0;
  const dwRequests = dealwork?.listing_pending_requests?.length ?? 0;

  return (
    <Card className="h-full border-zinc-800/70 bg-zinc-900/40 shadow-none transition-colors duration-200 hover:border-zinc-700/80">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between font-mono text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          <span className="flex items-center gap-2">
            <Radar className="size-3.5 text-emerald-400" aria-hidden="true" />
            Autonomous Ops
          </span>
          <Badge variant="outline" className="border-zinc-700 bg-zinc-800/40 font-mono text-[10px] text-zinc-500">
            {state.schedule.length} jobs
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0 space-y-4">
        {/* STE bounty radar */}
        <div className="rounded-lg border border-zinc-800/70 bg-zinc-950/50 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <span className="relative flex size-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex size-1.5 rounded-full bg-emerald-400" />
              </span>
              STE bounty radar
            </p>
            <p className="font-mono text-[10px] text-zinc-600">
              {radarOk ? `scanned ${timeAgo(radar.checked_at)}` : "no scan data"}
            </p>
          </div>
          <p className="mt-2 font-mono text-lg font-bold text-zinc-100">
            {radarOk ? openCount : "—"}
            <span className="ml-2 text-[11px] font-normal text-zinc-500">
              open agent listing{openCount === 1 ? "" : "s"}
            </span>
          </p>
          <p className="mt-0.5 font-mono text-[10px] leading-relaxed text-zinc-600">
            {radarOk
              ? newCount > 0
                ? `${newCount} NEW since last scan — submission trigger armed`
                : "no new listings since last scan · horizon 14d"
              : "radar state unavailable in this sync"}
          </p>
        </div>

        {/* Dework task radar — second autonomous scan surface */}
        <div className="rounded-lg border border-zinc-800/70 bg-zinc-950/50 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <Briefcase className="size-3 text-emerald-400/80" aria-hidden="true" />
              Dework task radar
            </p>
            <p className="font-mono text-[10px] text-zinc-600">
              {dwOk ? `scanned ${timeAgo(dealwork!.checked_at)}` : "no scan data"}
            </p>
          </div>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="font-mono text-lg font-bold tabular-nums text-zinc-100">
              {dwOk ? dwReal : "—"}
              <span className="ml-1.5 text-[11px] font-normal text-zinc-500">real tasks</span>
            </p>
            <p className="font-mono text-[11px] text-zinc-500">
              of a pool of <span className="tabular-nums text-zinc-400">{dwPool}</span>
            </p>
          </div>
          {/* truthful spam share bar: ads vs real in the bidding pool */}
          {dwOk && dwPool > 0 && (
            <div
              className="mt-2 flex h-1.5 w-full overflow-hidden rounded-full bg-zinc-800"
              role="img"
              aria-label={`Dework pool: ${dwReal} real, ${dwAds} ad-spam posts`}
            >
              <div
                className="h-full bg-emerald-500/70"
                style={{ width: `${Math.min(100, (dwReal / dwPool) * 100)}%` }}
              />
              <div
                className="h-full bg-zinc-600/70"
                style={{ width: `${Math.min(100, (dwAds / dwPool) * 100)}%` }}
              />
            </div>
          )}
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-zinc-600">
            {dwOk
              ? dwReal > 0
                ? `${dwReal} real task${dwReal > 1 ? "s" : ""} — investigate fit this wake (NA-13)`
                : dwNew > 0
                  ? `${dwNew} new pool item${dwNew > 1 ? "s" : ""} since last scan, all ad-spam so far · ${dwAds}/${dwPool} ads`
                  : `pool ${dwPool}, all ad-spam so far · ${dwRequests} listing request${dwRequests === 1 ? "" : "s"} pending`
              : "dealwork-jobs.json not present in this sync"}
          </p>
        </div>

        {/* cron schedule */}
        <div>
          <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
            Wake-up Schedule
          </p>
          <ul className="space-y-1.5">
            {state.schedule.map((s) => {
              const countdown = nextFireCountdown(s.expr, s.tz);
              return (
                <li
                  key={s.name}
                  className="flex items-center justify-between gap-2 rounded-md border border-zinc-800/60 bg-zinc-950/40 px-2.5 py-1.5"
                >
                  <span className="min-w-0 truncate font-mono text-[11px] text-zinc-300" title={s.name}>
                    {s.name}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    {countdown && (
                      <span className="font-mono text-[10px] tabular-nums text-emerald-400/80">{countdown}</span>
                    )}
                    <span className="font-mono text-[10px] tabular-nums text-zinc-500">{scheduleLabel(s)}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* system signers (public info only — keys never leave secrets/) */}
        {state.system_signers && Object.keys(state.system_signers).length > 0 && (
          <div>
            <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
              System Signers
            </p>
            <ul className="space-y-1.5">
              {Object.entries(state.system_signers).map(([key, signer]) => (
                <li
                  key={key}
                  className="rounded-md border border-zinc-800/60 bg-zinc-950/40 px-2.5 py-1.5"
                  title={signer.note ?? key}
                >
                  <p className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    <KeyRound className="size-3 text-amber-400/80" aria-hidden="true" />
                    {key.replace(/_/g, " ")}
                  </p>
                  <p className="mt-0.5 truncate font-mono text-[11px] text-zinc-300" title={signer.address}>
                    {signer.address}
                  </p>
                  <p className="mt-0.5 font-mono text-[10px] text-zinc-600">
                    created {signer.created_at?.slice(0, 10)} · 0 funds · key offline in secrets/
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------------------- session activity ----------------------------- */

export function SessionActivityCard({ data }: { data: MissionData }) {
  const sessions = data.session_activity ?? [];
  const max = Math.max(1, ...sessions.map((s) => s.actions));

  /* GitHub-style calendar heatmap — one cell per UTC calendar day from the
     first logged session to today (capped at the last 12 weeks), columns =
     weeks (Mon-start), rows = weekdays. Days with no logged actions render
     as honest empty cells; the mission timeline shows its real gaps. */
  const heat = useMemo(() => {
    const byDay = new Map<string, number>();
    for (const s of sessions) {
      const d = (s.first_ts ?? "").slice(0, 10);
      if (!d) continue;
      byDay.set(d, (byDay.get(d) ?? 0) + s.actions);
    }
    if (!byDay.size) return null;

    const today = new Date();
    const todayUtc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    const sortedDays = [...byDay.keys()].sort();
    let first = new Date(sortedDays[0] + "T00:00:00Z");
    const minStart = new Date(todayUtc.getTime() - 12 * 7 * 86400000);
    if (first < minStart) first = minStart;
    // align grid start to the Monday on/before the first active day
    first.setUTCDate(first.getUTCDate() - ((first.getUTCDay() + 6) % 7));

    const weeks: { label: string | null; days: { date: string; count: number }[] }[] = [];
    const cur = new Date(first);
    let lastMonth = -1;
    while (cur <= todayUtc) {
      const week = { label: null as string | null, days: [] as { date: string; count: number }[] };
      for (let i = 0; i < 7; i++) {
        const ds = cur.toISOString().slice(0, 10);
        week.days.push({ date: ds, count: cur <= todayUtc ? (byDay.get(ds) ?? 0) : -1 });
        cur.setUTCDate(cur.getUTCDate() + 1);
      }
      const m = new Date(week.days[0].date + "T00:00:00Z").getUTCMonth();
      if (m !== lastMonth) {
        week.label = new Date(week.days[0].date + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
        lastMonth = m;
      }
      weeks.push(week);
    }

    // weekday totals (Mon..Sun) for the busiest-weekday stat
    const weekdayTotals = new Array(7).fill(0) as number[];
    for (const [d, n] of byDay) {
      const wd = (new Date(d + "T00:00:00Z").getUTCDay() + 6) % 7;
      weekdayTotals[wd] += n;
    }
    const todayStr = todayUtc.toISOString().slice(0, 10);
    const maxDay = Math.max(1, ...byDay.values());
    return { weeks, todayStr, maxDay, weekdayTotals, total: [...byDay.values()].reduce((s, n) => s + n, 0) };
  }, [sessions]);

  const busiestWeekday = heat
    ? heat.weekdayTotals.reduce((best, n, i) => (n > heat.weekdayTotals[best] ? i : best), 0)
    : 0;
  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <Card className="h-full border-zinc-800/70 bg-zinc-900/40 shadow-none transition-colors duration-200 hover:border-zinc-700/80">
      <CardHeader className="pb-3">
        <CardTitle className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          Session Activity
          <span className="ml-2 font-normal normal-case tracking-normal text-zinc-600">
            logged actions per autonomous session
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="min-w-0">
        <div
          className="flex h-28 items-center gap-1 overflow-hidden sm:gap-2 sm:overflow-visible lg:gap-3"
          role="img"
          aria-label="Bar chart of logged actions per session"
        >
          {sessions.map((s, i) => (
            <div key={s.session} className="group flex min-w-0 flex-1 flex-col items-center gap-1.5 overflow-hidden">
              <span className="font-mono text-[10px] font-semibold text-zinc-400">{s.actions}</span>
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${Math.max((s.actions / max) * 100, 6)}%` }}
                transition={{ delay: i * 0.07, duration: 0.45, ease: "easeOut" }}
                className="w-full rounded-t bg-gradient-to-t from-emerald-500/30 to-emerald-400/70 transition-colors group-hover:to-emerald-300"
                title={`session ${s.session}: ${s.actions} actions (${(s.first_ts ?? "").slice(0, 10) || "date n/a"})`}
              />
              <span className="w-full truncate text-center font-mono text-[8px] uppercase tracking-wider text-zinc-600 sm:text-[9px]">
                S{s.session}
              </span>
            </div>
          ))}
        </div>
        {/* calendar heatmap — full timeline including quiet days */}
        {heat && heat.weeks.length > 0 && (
          <div className="mt-4 min-w-0 border-t border-zinc-800/60 pt-3">
            <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
              Action calendar
              <span className="ml-1.5 font-normal normal-case tracking-normal text-zinc-600">
                {heat.weeks.length} week{heat.weeks.length === 1 ? "" : "s"} · busiest {WEEKDAYS[busiestWeekday]} (
                {heat.weekdayTotals[busiestWeekday]})
              </span>
            </p>
            <div className="min-w-0 overflow-x-auto" role="img" aria-label="Calendar heatmap of logged actions per day; darker cells mean more actions">
              <div className="inline-flex min-w-0 flex-col gap-0.5">
                {/* month labels aligned to week columns */}
                <div className="flex gap-0.5 pl-6" aria-hidden="true">
                  {heat.weeks.map((w, i) => (
                    <span key={i} className="w-2.5 text-center font-mono text-[8px] text-zinc-600">
                      {w.label ? w.label.slice(0, 1) : ""}
                    </span>
                  ))}
                </div>
                <div className="flex gap-0.5">
                  {/* weekday rail: M · W · F */}
                  <div className="mr-1 flex w-5 shrink-0 flex-col gap-0.5" aria-hidden="true">
                    {["M", "", "W", "", "F", "", ""].map((l, i) => (
                      <span key={i} className="h-2.5 text-right font-mono text-[8px] leading-2.5 text-zinc-600">
                        {l}
                      </span>
                    ))}
                  </div>
                  {heat.weeks.map((w, wi) => (
                    <div key={wi} className="flex flex-col gap-0.5">
                      {w.days.map((d) =>
                        d.count < 0 ? (
                          <span key={d.date} className="size-2.5" />
                        ) : (
                          <motion.span
                            key={d.date}
                            initial={{ scale: 0.4, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: Math.min(wi * 0.03, 0.4), duration: 0.25 }}
                            className={`size-2.5 rounded-[3px] transition-transform hover:scale-125 focus-visible:scale-125 motion-reduce:transition-none ${
                              d.date === heat.todayStr
                                ? "ring-1 ring-emerald-300/80 " +
                                  (d.count > 0
                                    ? intensityClass(d.count, heat.maxDay)
                                    : "bg-zinc-800")
                                : d.count > 0
                                  ? intensityClass(d.count, heat.maxDay)
                                  : "bg-zinc-800/50"
                            }`}
                            title={`${d.date}: ${d.count} action${d.count === 1 ? "" : "s"}${d.date === heat.todayStr ? " · today" : ""}`}
                            tabIndex={0}
                          />
                        ),
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* intensity legend */}
            <div className="mt-1.5 flex items-center gap-1.5 font-mono text-[8px] uppercase tracking-wider text-zinc-600">
              <span>less</span>
              <span className="size-2 rounded-[3px] bg-zinc-800/50" aria-hidden="true" />
              <span className="size-2 rounded-[3px] bg-emerald-500/20" aria-hidden="true" />
              <span className="size-2 rounded-[3px] bg-emerald-500/40" aria-hidden="true" />
              <span className="size-2 rounded-[3px] bg-emerald-500/65" aria-hidden="true" />
              <span className="size-2 rounded-[3px] bg-emerald-400" aria-hidden="true" />
              <span>more</span>
            </div>
          </div>
        )}
        <p className="mt-2 text-[11px] leading-snug text-zinc-600">
          Honest accounting — every bar is an action recorded in actions.json with evidence. Highest
          single-session count: {max === 1 && sessions.every((s) => s.actions === 1) ? "1" : max}.
        </p>
      </CardContent>
    </Card>
  );
}

/* heat cell intensity — 4 honest tiers relative to the busiest day */
function intensityClass(count: number, maxDay: number): string {
  const ratio = count / maxDay;
  if (ratio > 0.75) return "bg-emerald-400";
  if (ratio > 0.5) return "bg-emerald-500/65";
  if (ratio > 0.25) return "bg-emerald-500/40";
  return "bg-emerald-500/20";
}
